import {
  alpha,
  Components,
  ComponentsOverrides,
  ComponentsProps,
  ComponentsVariants,
} from '@mui/material/styles'
import { buttonClasses } from '@mui/material'
import { palette } from './palette'

const MuiButton: {
  defaultProps?: ComponentsProps['MuiButton']
  styleOverrides?: ComponentsOverrides['MuiButton']
  variants?: ComponentsVariants['MuiButton']
} = {
  styleOverrides: {
    root: {
      letterSpacing: '1.25px',
      lineHeight: '1.75rem',
    },
  },
  variants: [
    {
      props: {
        disabled: true,
      },
      style: {
        color: palette.black.disabled,
        borderColor: palette.black.disabled,
      },
    },
    {
      props: {
        disabled: true,
        variant: 'contained',
      },
      style: {
        background: palette.gray.chipLight,
      },
    },
    {
      props: {
        variant: 'contained',
      },
      style: {
        boxShadow:
          '0px 1px 5px rgba(0, 0, 0, 0.2), 0px 3px 4px rgba(0, 0, 0, 0.12), 0px 2px 4px rgba(0, 0, 0, 0.14);',
        '&:hover': {
          boxShadow:
            '0px 1px 5px rgba(0, 0, 0, 0.2), 0px 3px 4px rgba(0, 0, 0, 0.12), 0px 2px 4px rgba(0, 0, 0, 0.14);',
        },
        '&:focus': {
          boxShadow:
            '0px 1px 5px rgba(0, 0, 0, 0.2), 0px 3px 4px rgba(0, 0, 0, 0.12), 0px 2px 4px rgba(0, 0, 0, 0.14);',
        },
        '&:active': {
          boxShadow:
            '0px 0px 2px rgba(0, 0, 0, 0.12), 0px 2px 2px rgba(0, 0, 0, 0.24);',
        },
      },
    },
    {
      props: {
        disableElevation: true,
      },
      style: {
        boxShadow: 'none',
        '&:hover, &:focus, &:active': {
          boxShadow: 'none',
        },
      },
    },
    {
      props: {
        color: 'primary',
        variant: 'contained',
      },
      style: {
        '&:hover': {
          background: palette.primary.A200,
        },
        '&:focus, &:active': {
          background: palette.primary.A100,
        },
      },
    },
    {
      props: {
        color: 'primary',
        variant: 'outlined',
      },
      style: {
        '&:hover': {
          background: alpha(palette.primary.main, 0.04),
        },
        '&:focus, &:active': {
          background: alpha(palette.primary.main, 0.12),
        },
      },
    },
    {
      props: {
        color: 'primary',
        variant: 'text',
      },
      style: {
        '&:hover': {
          background: alpha(palette.primary.main, 0.04),
        },
        '&:focus, &:active': {
          background: alpha(palette.primary.main, 0.12),
        },
      },
    },
    {
      props: {
        color: 'secondary',
        variant: 'contained',
      },
      style: {
        '&:hover': {
          background: palette.secondary.A200,
        },
        '&:focus, &:active': {
          background: palette.secondary.A100,
        },
      },
    },
    {
      props: {
        color: 'secondary',
        variant: 'outlined',
      },
      style: {
        '&:hover': {
          background: alpha(palette.secondary.main, 0.04),
        },
        '&:focus, &:active': {
          background: alpha(palette.secondary.main, 0.12),
        },
      },
    },
    {
      props: {
        color: 'secondary',
        variant: 'text',
      },
      style: {
        '&:hover': {
          background: alpha(palette.secondary.main, 0.04),
        },
        '&:focus, &:active': {
          background: alpha(palette.secondary.main, 0.12),
        },
      },
    },
    {
      props: {
        color: 'error',
        variant: 'contained',
      },
      style: {
        background: palette.error.dark,
        '&:hover': {
          background: palette.error.A200,
        },
        '&:focus, &:active': {
          background: palette.error.A100,
        },
      },
    },
    {
      props: {
        color: 'error',
        variant: 'outlined',
      },
      style: {
        color: palette.error.dark,
        borderColor: palette.error.dark,
        '&:hover': {
          background: alpha(palette.error.main, 0.04),
        },
        '&:focus, &:active': {
          background: alpha(palette.error.main, 0.12),
        },
      },
    },
    {
      props: {
        color: 'error',
        variant: 'text',
      },
      style: {
        color: palette.error.dark,
        borderColor: palette.error.dark,
        '&:hover': {
          background: alpha(palette.error.main, 0.04),
        },
        '&:focus, &:active': {
          background: alpha(palette.error.main, 0.12),
        },
      },
    },
    {
      props: {
        color: 'success',
        variant: 'contained',
      },
      style: {
        background: palette.success.dark,
        '&:hover': {
          background: palette.success.A200,
        },
        '&:focus, &:active': {
          background: palette.success.A100,
        },
      },
    },
    {
      props: {
        color: 'success',
        variant: 'outlined',
      },
      style: {
        color: palette.success.dark,
        borderColor: palette.success.dark,
        '&:hover': {
          background: alpha(palette.success.main, 0.04),
        },
        '&:focus, &:active': {
          background: alpha(palette.success.main, 0.12),
        },
      },
    },
    {
      props: {
        color: 'success',
        variant: 'text',
      },
      style: {
        color: palette.success.dark,
        borderColor: palette.success.dark,
        '&:hover': {
          background: alpha(palette.success.main, 0.04),
        },
        '&:focus, &:active': {
          background: alpha(palette.success.main, 0.12),
        },
      },
    },
    {
      props: {
        color: 'warning',
        variant: 'contained',
      },
      style: {
        background: palette.warning.dark,
        '&:hover': {
          background: palette.warning.A200,
        },
        '&:focus, &:active': {
          background: palette.warning.A100,
        },
      },
    },
    {
      props: {
        color: 'warning',
        variant: 'outlined',
      },
      style: {
        color: palette.warning.dark,
        borderColor: palette.warning.dark,
        '&:hover': {
          background: alpha(palette.warning.main, 0.04),
        },
        '&:focus, &:active': {
          background: alpha(palette.warning.main, 0.12),
        },
      },
    },
    {
      props: {
        color: 'warning',
        variant: 'text',
      },
      style: {
        color: palette.warning.dark,
        borderColor: palette.warning.dark,
        '&:hover': {
          background: alpha(palette.warning.main, 0.04),
        },
        '&:focus, &:active': {
          background: alpha(palette.warning.main, 0.12),
        },
      },
    },
    {
      props: {
        color: 'gray',
      },
      style: {
        color: palette.black.inactive,
        borderColor: palette.gray.inactive,
        '&:hover, &:focus, &:active': {
          borderColor: palette.gray.inactive,
        },
      },
    },
    {
      props: {
        color: 'gray',
        variant: 'contained',
      },
      style: {
        background: palette.gray.chipLight,
        '&:hover': {
          background: palette.gray.A200,
        },
        '&:focus, &:active': {
          background: palette.gray.A100,
        },
      },
    },
    {
      props: {
        color: 'gray',
        variant: 'outlined',
      },
      style: {
        '&:hover': {
          background: palette.hoverOverlay.blackLight,
        },
        '&:focus, &:active': {
          background: palette.hoverOverlay.black,
        },
      },
    },
    {
      props: {
        color: 'gray',
        variant: 'text',
      },
      style: {
        '&:hover': {
          background: palette.hoverOverlay.blackLight,
        },
        '&:focus, &:active': {
          background: palette.hoverOverlay.black,
        },
      },
    },
    {
      props: {
        color: 'white',
      },
      style: {
        color: palette.white.primary,
        borderColor: palette.white.primary,
        '&:hover, &:focus, &:active': {
          borderColor: palette.white.primary,
        },
        [`&.${buttonClasses.disabled}`]: {
          color: `${palette.white.disabled}`,
        },
      },
    },
    {
      props: {
        color: 'white',
        variant: 'contained',
      },
      style: {
        background: palette.white.primary,
        color: palette.black.main,
        '&:hover': {
          background: palette.white.A200,
        },
        '&:focus, &:active': {
          background: palette.white.A100,
        },
        [`&.${buttonClasses.disabled}`]: {
          background: `${alpha(palette.white.primary, 0.16)}`,
        },
      },
    },
    {
      props: {
        color: 'white',
        variant: 'outlined',
      },
      style: {
        '&:hover': {
          background: palette.hoverOverlay.white,
        },
        '&:focus, &:active': {
          background: alpha(palette.white.primary, 0.16),
        },
        [`&.${buttonClasses.disabled}`]: {
          borderColor: `${alpha(palette.white.primary, 0.16)}`,
        },
      },
    },
    {
      props: {
        color: 'white',
        variant: 'text',
      },
      style: {
        '&:hover': {
          background: palette.hoverOverlay.white,
        },
        '&:focus, &:active': {
          background: alpha(palette.white.primary, 0.16),
        },
      },
    },
    {
      props: {
        size: 'tiny',
      },
      style: {
        fontSize: '12px',
        padding: '4px 8px',
        lineHeight: '16px',
        borderRadius: '2px',
      },
    },
  ],
}

const MuiToggleButton: {
  defaultProps?: ComponentsProps['MuiToggleButton']
  styleOverrides?: ComponentsOverrides['MuiToggleButton']
  variants?: ComponentsVariants['MuiToggleButton']
} = {
  variants: [
    {
      props: {
        color: 'gray',
      },
      style: {
        '&.Mui-selected': {
          background: palette.gray.chipLight,
          color: palette.black.inactive,
          '&:hover': {
            background: palette.gray.A200,
          },
        },
      },
    },
  ],
}

const MuiIconButton: {
  defaultProps?: ComponentsProps['MuiIconButton']
  styleOverrides?: ComponentsOverrides['MuiIconButton']
  variants?: ComponentsVariants['MuiIconButton']
} = {
  variants: [
    {
      props: {
        color: 'gray',
      },
      style: {
        color: palette.black.inactive,
      },
    },
  ],
}

const MuiBreadcrumbs: {
  defaultProps?: ComponentsProps['MuiBreadcrumbs']
  styleOverrides?: ComponentsOverrides['MuiBreadcrumbs']
  variants?: ComponentsVariants['MuiBreadcrumbs']
} = {
  styleOverrides: {
    root: {
      color: palette.black.medium,
      display: 'flex',
      alignItems: 'center',
    },
    separator: {
      marginLeft: 0,
      marginRight: 0,
    },
    ol: {
      flexWrap: 'nowrap',
    },
    li: {
      whiteSpace: 'nowrap',
      '& > a': {
        display: 'flex',
        alignItems: 'center',
      },
    },
  },
}

const MuiFormControlLabel: {
  defaultProps?: ComponentsProps['MuiFormControlLabel']
  styleOverrides?: ComponentsOverrides['MuiFormControlLabel']
  variants?: ComponentsVariants['MuiFormControlLabel']
} = {
  styleOverrides: {
    root: {
      color: palette.black.inactive,
    },
  },
}

export const components: Components = {
  MuiButton,
  MuiBreadcrumbs,
  MuiToggleButton,
  MuiIconButton,
  MuiFormControlLabel,
}
